import { BOARD_SIZE, SQUARE_COUNT } from '../constants';
import type { Side } from './types';

const FILE_LETTERS = 'abcdefgh';

export const squareIndex = (file: number, rank: number): number => rank * BOARD_SIZE + file;
export const fileOf = (square: number): number => square % BOARD_SIZE;
export const rankOf = (square: number): number => Math.floor(square / BOARD_SIZE);

export const isOnBoard = (file: number, rank: number): boolean =>
  file >= 0 && file < BOARD_SIZE && rank >= 0 && rank < BOARD_SIZE;

export const squareName = (square: number): string =>
  `${FILE_LETTERS[fileOf(square)]}${rankOf(square) + 1}`;

export const ALL_SQUARES: readonly number[] = Array.from({ length: SQUARE_COUNT }, (_, i) => i);

const NEIGHBOR_OFFSETS: readonly (readonly [number, number])[] = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

export const neighborSquares = (square: number): number[] => {
  const file = fileOf(square);
  const rank = rankOf(square);
  const result: number[] = [];
  for (const [df, dr] of NEIGHBOR_OFFSETS) {
    if (isOnBoard(file + df, rank + dr)) result.push(squareIndex(file + df, rank + dr));
  }
  return result;
};

/** A knight move goes two squares on one line and one on the other, so the two counts multiply to 2. */
const KNIGHT_STEP_PRODUCT = 2;

/**
 * The squares where a move starts, turns, and ends. A knight move turns 90
 * degrees once, after the two steps of its long leg. Every other move is one
 * straight leg. Garrett, 2026-10-15: "knights should show arrows in the same
 * 90 degree angle they would move, so its clear a trapdoor would take them out".
 */
export const movePath = (from: number, to: number): number[] => {
  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);
  if (Math.abs(df * dr) !== KNIGHT_STEP_PRODUCT) return [from, to];
  const corner =
    Math.abs(dr) > Math.abs(df)
      ? squareIndex(fileOf(from), rankOf(to))
      : squareIndex(fileOf(to), rankOf(from));
  return [from, corner, to];
};

/** Gold starts on ranks 0 and 1 and marches up. Violet marches down. */
export const forwardDirection = (side: Side): number => (side === 'gold' ? 1 : -1);
export const homeRank = (side: Side): number => (side === 'gold' ? 0 : BOARD_SIZE - 1);
export const promotionRank = (side: Side): number => (side === 'gold' ? BOARD_SIZE - 1 : 0);
export const pawnStartRank = (side: Side): number => (side === 'gold' ? 1 : BOARD_SIZE - 2);

export const isOnOwnHalf = (square: number, side: Side): boolean =>
  side === 'gold' ? rankOf(square) < BOARD_SIZE / 2 : rankOf(square) >= BOARD_SIZE / 2;

/** Ranks counted from a side's home edge, zero based. */
export const ranksAdvanced = (square: number, side: Side): number =>
  side === 'gold' ? rankOf(square) : BOARD_SIZE - 1 - rankOf(square);

export const isWithinBackRanks = (square: number, side: Side, rankCount: number): boolean =>
  ranksAdvanced(square, side) < rankCount;
